import type { UserBillingData } from "../../api/admin-revenue";
import { t } from "../../i18n";
import { GrantAmountInput, UserSummary } from "./BillingSummary";
import {
  EntitlementEventsTable,
  LedgerTable,
  PaymentTransactionsTable,
  UsageEventsTable,
  WebhookTable,
} from "./BillingTables";
import { billingPageStyles as s } from "./billing-page-styles";

export type BillingDialogKind = "grant" | "resync" | "replay" | null;

export function UserBillingPanel({
  userId,
  billing,
  loading,
  dialog,
  grantAmount,
  onSearch,
  onUserIdChange,
  onGrantAmountChange,
  onDialogChange,
  onDeveloperCredit,
  onReplayTarget,
}: {
  userId: string;
  billing: UserBillingData | null;
  loading: boolean;
  dialog: BillingDialogKind;
  grantAmount: string;
  onSearch: () => void;
  onUserIdChange: (value: string) => void;
  onGrantAmountChange: (value: string) => void;
  onDialogChange: (dialog: BillingDialogKind) => void;
  onDeveloperCredit: () => void;
  onReplayTarget: (id: string) => void;
}): React.JSX.Element {
  return (
    <>
      <SearchBar
        userId={userId}
        loading={loading}
        onChange={onUserIdChange}
        onSearch={onSearch}
      />
      {billing && (
        <LoadedUserBilling
          userId={userId.trim()}
          billing={billing}
          dialog={dialog}
          grantAmount={grantAmount}
          onGrantAmountChange={onGrantAmountChange}
          onDialogChange={onDialogChange}
          onDeveloperCredit={onDeveloperCredit}
          onReplayTarget={onReplayTarget}
        />
      )}
    </>
  );
}

function LoadedUserBilling({
  userId,
  billing,
  dialog,
  grantAmount,
  onGrantAmountChange,
  onDialogChange,
  onDeveloperCredit,
  onReplayTarget,
}: {
  userId: string;
  billing: UserBillingData;
  dialog: BillingDialogKind;
  grantAmount: string;
  onGrantAmountChange: (value: string) => void;
  onDialogChange: (dialog: BillingDialogKind) => void;
  onDeveloperCredit: () => void;
  onReplayTarget: (id: string) => void;
}): React.JSX.Element {
  return (
    <>
      <UserSummary
        userId={userId}
        billing={billing}
        onGrant={() => onDialogChange("grant")}
        onResync={() => onDialogChange("resync")}
        onDeveloperCredit={onDeveloperCredit}
      />
      {dialog === "grant" && (
        <GrantAmountInput value={grantAmount} onChange={onGrantAmountChange} />
      )}
      <BillingDataTables
        billing={billing}
        onReplayTarget={onReplayTarget}
        onDialogChange={onDialogChange}
      />
    </>
  );
}

function BillingDataTables({
  billing,
  onReplayTarget,
  onDialogChange,
}: {
  billing: UserBillingData;
  onReplayTarget: (id: string) => void;
  onDialogChange: (dialog: BillingDialogKind) => void;
}): React.JSX.Element {
  return (
    <>
      <LedgerTable entries={billing.recent_ledger} />
      <UsageEventsTable entries={billing.recent_usage_events} />
      <PaymentTransactionsTable entries={billing.recent_payment_transactions} />
      <EntitlementEventsTable entries={billing.recent_entitlement_events} />
      <WebhookTable
        events={billing.recent_webhooks}
        onReplay={(id) => {
          onReplayTarget(id);
          onDialogChange("replay");
        }}
      />
    </>
  );
}

function SearchBar({
  userId,
  loading,
  onChange,
  onSearch,
}: {
  userId: string;
  loading: boolean;
  onChange: (v: string) => void;
  onSearch: () => void;
}): React.JSX.Element {
  return (
    <div style={s.searchRow}>
      <input
        type="text"
        value={userId}
        placeholder={t("billing.search_placeholder")}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSearch()}
        style={s.searchInput}
      />
      <button
        type="button"
        onClick={onSearch}
        disabled={loading}
        style={s.searchBtn}
      >
        {loading ? t("common.loading") : t("common.search")}
      </button>
    </div>
  );
}
